// =============================================================================
// Accountant Service
// =============================================================================
// API client for accountant portal (temporary access users)
// =============================================================================

import api from './api';

// =============================================================================
// TOTP Session Helpers
// =============================================================================
// Tracks whether accountant has verified TOTP this session.
// Uses sessionStorage so it auto-clears on tab/browser close.

const TOTP_SESSION_KEY = 'accountant_totp_verified';

export function hasTotpSession(): boolean {
  return sessionStorage.getItem(TOTP_SESSION_KEY) === 'true';
}

export function setTotpSession(): void {
  sessionStorage.setItem(TOTP_SESSION_KEY, 'true');
}

export function clearTotpSession(): void {
  sessionStorage.removeItem(TOTP_SESSION_KEY);
}

// =============================================================================
// Types
// =============================================================================

export interface AccountantDashboard {
  totalInvestors: number;
  totalProfiles: number;
  k1DocumentsUploaded: number;
  pendingK1s: number;
  currentTaxYear: number;
}

export interface AccountantInvestorProfile {
  id: string;
  investorId: string;
  investorName: string;
  investorEmail: string;
  type: 'INDIVIDUAL' | 'LLC' | 'TRUST' | 'CORPORATION' | 'PARTNERSHIP' | 'IRA';
  legalName?: string;
  entityName?: string;
  displayName: string;
  hasSSN: boolean;
  ssnLastFour?: string;
  hasFEIN: boolean;
  feinLastFour?: string;
  totalInvested: number;
  investmentCount: number;
  k1Count: number;
}

export interface AccountantProfileDetail {
  id: string;
  type: 'INDIVIDUAL' | 'LLC' | 'TRUST' | 'CORPORATION' | 'PARTNERSHIP' | 'IRA';
  legalName?: string;
  entityName?: string;
  authorizedSigner?: string;
  stateOfFormation?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  hasSSN: boolean;
  ssnLastFour?: string;
  hasFEIN: boolean;
  feinLastFour?: string;
  investments: {
    id: string;
    dealId: string;
    dealName: string;
    investmentAmount: number;
    ownershipPercentage: number;
  }[];
  k1Documents: {
    id: string;
    name: string;
    taxYear: number;
    createdAt: string;
  }[];
  investor: {
    id: string;
    name: string;
    email: string;
  };
}

export interface AccountantDistributionSummary {
  year: number;
  totalDistributed: number;
  distributionCount: number;
  byDeal: {
    dealId: string;
    dealName: string;
    totalDistributed: number;
    distributions: {
      id: string;
      date: string;
      type: string;
      amount: number;
      notes?: string;
    }[];
  }[];
}

export interface AccountantDocument {
  id: string;
  name: string;
  type: string;
  taxYear?: number;
  dealId?: string;
  dealName?: string;
  profileId?: string;
  profileName?: string;
  createdAt: string;
  mimeType?: string;
}

export interface AccountantDeal {
  id: string;
  name: string;
  propertyType: string;
  status: string;
  investorCount: number;
  k1Count: number;
  taxYears: number[];
  address: string;
}

export interface AccountantDealInvestor {
  investmentId: string;
  profileId: string;
  type: 'INDIVIDUAL' | 'LLC' | 'TRUST' | 'CORPORATION' | 'PARTNERSHIP' | 'IRA';
  displayName: string;
  investorName: string;
  investorEmail: string;
  investmentAmount: number;
  ownershipPercentage: number;
  hasK1: boolean;
  k1Count: number;
  k1Documents: {
    id: string;
    name: string;
    createdAt: string;
  }[];
}

export interface AccountantDealInvestorsResponse {
  deal: {
    id: string;
    name: string;
    propertyType: string;
    status: string;
  };
  taxYear: number | null;
  availableTaxYears: number[];
  investors: AccountantDealInvestor[];
  totalInvestors: number;
  investorsWithK1: number;
  investorsWithoutK1: number;
}

// =============================================================================
// Accountant Service
// =============================================================================

export const accountantService = {
  // ===========================================================================
  // Deals (Tax Portal Flow)
  // ===========================================================================

  async getDeals(): Promise<AccountantDeal[]> {
    const response = await api.get<{ success: boolean; data: AccountantDeal[] }>(
      '/accountant/deals'
    );
    return response.data.data;
  },

  async getDealInvestors(dealId: string, taxYear?: number): Promise<AccountantDealInvestorsResponse> {
    const params = taxYear ? `?taxYear=${taxYear}` : '';
    const response = await api.get<{ success: boolean; data: AccountantDealInvestorsResponse }>(
      `/accountant/deals/${dealId}/investors${params}`
    );
    return response.data.data;
  },

  async getDealFinancials(dealId: string, taxYear?: number): Promise<AccountantDocument[]> {
    const params = taxYear ? `?taxYear=${taxYear}` : '';
    const response = await api.get<{ success: boolean; data: AccountantDocument[] }>(
      `/accountant/deals/${dealId}/financials${params}`
    );
    return response.data.data;
  },

  async exportPartnerInfo(dealId: string, taxYear: number): Promise<void> {
    const response = await api.get(
      `/accountant/deals/${dealId}/export-partner-info?taxYear=${taxYear}`,
      { responseType: 'blob' }
    );

    // Trigger browser download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Partner_Info_${taxYear}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // ===========================================================================
  // Dashboard
  // ===========================================================================

  async getDashboard(): Promise<AccountantDashboard> {
    const response = await api.get<{ success: boolean; data: AccountantDashboard }>(
      '/accountant/dashboard'
    );
    return response.data.data;
  },

  // ===========================================================================
  // Investors
  // ===========================================================================

  async listInvestors(search?: string): Promise<AccountantInvestorProfile[]> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get<{ success: boolean; data: AccountantInvestorProfile[] }>(
      `/accountant/investors${params}`
    );
    return response.data.data;
  },

  async getInvestorProfile(profileId: string): Promise<AccountantProfileDetail> {
    const response = await api.get<{ success: boolean; data: AccountantProfileDetail }>(
      `/accountant/investors/${profileId}`
    );
    return response.data.data;
  },

  async getProfileDistributions(profileId: string, year?: number): Promise<AccountantDistributionSummary> {
    const params = year ? `?year=${year}` : '';
    const response = await api.get<{ success: boolean; data: AccountantDistributionSummary }>(
      `/accountant/investors/${profileId}/distributions${params}`
    );
    return response.data.data;
  },

  async revealSensitiveData(
    profileId: string,
    dataType: 'ssn' | 'fein'
  ): Promise<{ value: string; expiresIn: number }> {
    const response = await api.get<{
      success: boolean;
      data: { value: string; expiresIn: number };
    }>(`/accountant/investors/${profileId}/sensitive/${dataType}`);
    return response.data.data;
  },

  // ===========================================================================
  // Documents
  // ===========================================================================

  async listDocuments(filters?: {
    type?: string;
    profileId?: string;
    taxYear?: number;
  }): Promise<AccountantDocument[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.profileId) params.append('profileId', filters.profileId);
    if (filters?.taxYear) params.append('taxYear', filters.taxYear.toString());

    const queryString = params.toString();
    const response = await api.get<{ success: boolean; data: AccountantDocument[] }>(
      `/accountant/documents${queryString ? `?${queryString}` : ''}`
    );
    return response.data.data;
  },

  async getDocumentViewUrl(documentId: string): Promise<{
    embedUrl: string;
    downloadUrl: string;
    previewUrl: string;
    proxyUrl: string;
    fileName: string;
    fileType: string;
    mimeType: string;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        embedUrl: string;
        downloadUrl: string;
        previewUrl: string;
        fileName: string;
        fileType: string;
        mimeType: string;
      };
    }>(`/accountant/documents/${documentId}/view-url`);

    // Fetch the file through our backend proxy and create a blob URL
    let proxyUrl = '';
    try {
      const fileResponse = await api.get(`/accountant/documents/${documentId}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([fileResponse.data], { type: response.data.data.mimeType });
      proxyUrl = URL.createObjectURL(blob);
    } catch (err) {
      console.error('Failed to fetch document via proxy:', err);
      proxyUrl = response.data.data.embedUrl;
    }

    return {
      ...response.data.data,
      proxyUrl,
    };
  },

  async uploadK1Document(
    profileId: string,
    file: File,
    taxYear: number,
    dealId?: string
  ): Promise<{ id: string; name: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('profileId', profileId);
    formData.append('taxYear', taxYear.toString());
    if (dealId) formData.append('dealId', dealId);

    const response = await api.post<{ success: boolean; data: { id: string; name: string } }>(
      '/accountant/documents',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data.data;
  },

  async deleteDocument(documentId: string): Promise<void> {
    await api.delete(`/accountant/documents/${documentId}`);
  },
};

export default accountantService;
